import React, { useState } from 'react';
import CategoryPicker from './CategoryPicker';
import { useToast } from './ToastContext';
import { FaTimes } from 'react-icons/fa';

const API_BASE = '/api';

interface FlatCategory {
  id: string;
  label: string;
}

interface NewDocRow {
  tempId: string;
  file: File;
  documentName: string;
  categoryId: string | null;
  indexPrefix: string;
  indexSuffix: string;
}

const INDEX_SUFFIX_PATTERN = /^\d{3}[a-zA-Z]?$/;

function cleanTitle(filename: string): string {
  const name = filename.replace(/\.[^/.]+$/, '');
  return name.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();
}

interface NewDocumentsPanelProps {
  flatCategories: FlatCategory[];
}

function NewDocumentsPanel({ flatCategories }: NewDocumentsPanelProps) {
  const { showToast } = useToast();
  const [rows, setRows] = useState<NewDocRow[]>([]);
  const [bulkCategoryId, setBulkCategoryId] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commitResult, setCommitResult] = useState<{
    created_document_ids: string[];
    errors: { file: string; reason: string }[];
  } | null>(null);
  const TimesIcon = FaTimes as unknown as React.FC<{ className?: string }>;

  const fetchPrefix = async (categoryId: string): Promise<string> => {
    try {
      const res = await fetch(`${API_BASE}/categories/${categoryId}/prefix`);
      const data = await res.json();
      return data.prefix || '';
    } catch {
      return '';
    }
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    const newRows: NewDocRow[] = newFiles.map((file) => ({
      tempId: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      documentName: cleanTitle(file.name),
      categoryId: null,
      indexPrefix: '',
      indexSuffix: '',
    }));
    setRows((prev) => [...prev, ...newRows]);
    e.target.value = '';
  };

  const removeRow = (tempId: string) => {
    setRows((prev) => prev.filter((r) => r.tempId !== tempId));
  };

  const updateRow = (tempId: string, changes: Partial<NewDocRow>) => {
    setRows((prev) => prev.map((r) => (r.tempId === tempId ? { ...r, ...changes } : r)));
  };

  const updateRowCategory = async (tempId: string, categoryId: string) => {
    updateRow(tempId, { categoryId, indexPrefix: '', indexSuffix: '' });
    const prefix = await fetchPrefix(categoryId);
    updateRow(tempId, { indexPrefix: prefix });
  };

  const applyBulkCategory = async () => {
    if (!bulkCategoryId) return;
    const prefix = await fetchPrefix(bulkCategoryId);
    setRows((prev) =>
      prev.map((r) => ({ ...r, categoryId: bulkCategoryId, indexPrefix: prefix, indexSuffix: r.indexSuffix }))
    );
  };

  const isValid =
    rows.length > 0 &&
    rows.every(
      (r) =>
        r.categoryId &&
        r.documentName.trim().length > 0 &&
        (!r.indexPrefix || INDEX_SUFFIX_PATTERN.test(r.indexSuffix))
    );

  const handleCommit = async () => {
    if (!isValid) return;

    setCommitting(true);
    setError(null);

    try {
      const formData = new FormData();
      rows.forEach((r) => formData.append('files', r.file));

      const itemsMeta = rows.map((r) => ({
        document_name: r.documentName.trim(),
        document_index: r.indexPrefix ? `${r.indexPrefix}${r.indexSuffix}` : '',
        category_id: r.categoryId,
      }));
      formData.append('items_meta', JSON.stringify(itemsMeta));

      const res = await fetch(`${API_BASE}/imports/new-documents-commit`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      setCommitResult(data);

      if (data.errors && data.errors.length > 0) {
        showToast(`Completed with ${data.errors.length} error(s).`, 'error');
      } else {
        showToast(`${data.created_document_ids.length} document(s) created successfully.`);
      }
    } catch (err: any) {
      setError('Commit failed');
      showToast('Import failed.', 'error');
    } finally {
      setCommitting(false);
    }
  };

  if (commitResult) {
    return (
      <div>
        <h2>Import Complete</h2>
        <p>{commitResult.created_document_ids.length} document(s) created.</p>
        {commitResult.errors.length > 0 && (
          <>
            <p className="error">{commitResult.errors.length} file(s) failed:</p>
            <ul>
              {commitResult.errors.map((e, idx) => (
                <li key={idx}>{e.file} — {e.reason}</li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="form-field">
        <label>Files</label>
        <label className="file-upload-button">
          Choose Files
          <input type="file" multiple className="file-upload-input" onChange={handleFilesSelected} />
        </label>
      </div>

      {rows.length > 0 && (
        <>
          <div className="bulk-category-bar">
            <CategoryPicker
              categories={flatCategories}
              value={bulkCategoryId}
              onChange={(id) => setBulkCategoryId(id)}
              placeholder="-- set category for all --"
            />
            <button className="btn-secondary" onClick={applyBulkCategory} disabled={!bulkCategoryId}>
              Apply to All
            </button>
          </div>

          <div className="doc-table-wrapper bulk-new-docs-table">
            <table className="doc-table">
              <thead>
                <tr>
                  <th className="col-filename">File</th>
                  <th className="col-target">Category</th>
                  <th className="col-index">Index</th>
                  <th className="col-name">Document Name</th>
                  <th className="col-title-preview">Title</th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const suffixValid = INDEX_SUFFIX_PATTERN.test(row.indexSuffix);
                  return (
                    <tr key={row.tempId}>
                      <td className="col-filename">{row.file.name}</td>
                      <td className="col-target">
                        <CategoryPicker
                          categories={flatCategories}
                          value={row.categoryId}
                          onChange={(id) => updateRowCategory(row.tempId, id)}
                        />
                      </td>
                      <td className="col-index">
                        {row.indexPrefix ? (
                          <>
                            <div className="index-input-group">
                              <span className="index-prefix-lock">{row.indexPrefix}</span>
                              <input
                                type="text"
                                className="index-suffix-input"
                                value={row.indexSuffix}
                                onChange={(e) => updateRow(row.tempId, { indexSuffix: e.target.value })}
                                placeholder="001"
                                maxLength={4}
                              />
                            </div>
                            {row.indexSuffix && !INDEX_SUFFIX_PATTERN.test(row.indexSuffix) && (
                              <p className="error error-small">3 digits + optional letter</p>
                            )}
                          </>
                        ) : (
                          <span className="modal-static-value-muted">No index</span>
                        )}
                      </td>
                      <td className="col-name">
                        <input
                          type="text"
                          value={row.documentName}
                          onChange={(e) => updateRow(row.tempId, { documentName: e.target.value })}
                        />
                      </td>
                      <td className="col-title-preview">
                        {row.documentName.trim() ? (
                          <span className="doc-name">
                            {row.indexPrefix
                              ? `${row.indexPrefix}${row.indexSuffix} - ${row.documentName.trim()}`
                              : row.documentName.trim()}
                          </span>
                        ) : (
                          <span className="modal-static-value-muted">—</span>
                        )}
                      </td>
                      <td className="col-actions">
                        <button className="icon-button icon-button-danger" title="Remove" onClick={() => removeRow(row.tempId)}>
                          <TimesIcon />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {error && <p className="error">{error}</p>}

      <button
        className="btn-primary btn-commit"
        onClick={handleCommit}
        disabled={committing || !isValid}
      >
        {committing ? 'Committing...' : `Commit ${rows.length} document(s)`}
      </button>
    </div>
  );
}

export default NewDocumentsPanel;